/// <reference types="vite/client" />
import React, { useEffect, useRef, useState, type CSSProperties } from "react";
import css from "../../static/css/comic-page.css?raw";
import type { Bubble, EditorState, Panel, PanelPlacement, Selection } from "./types";

export interface PageImage { id: string; order?: number; current_asset_url: string; content?: Panel["content"]; }
export interface PageLayerProps {
  state: EditorState;
  panels: PageImage[];
  selection?: Selection;
  showGuides?: boolean;
  onSelect?: (selection: Selection) => void;
  onGesture?: (event: React.PointerEvent<HTMLElement>, bubble: Bubble, mode: "drag" | "resize") => void;
  onPanelImageGesture?: (event: React.PointerEvent<HTMLElement>, placement: PanelPlacement) => void;
}

export function textExceedsBubble({
  textScrollWidth,
  textScrollHeight,
  bubbleClientWidth,
  bubbleClientHeight,
  horizontalPadding,
  verticalPadding,
}: {
  textScrollWidth: number;
  textScrollHeight: number;
  bubbleClientWidth: number;
  bubbleClientHeight: number;
  horizontalPadding: number;
  verticalPadding: number;
}): boolean {
  return textScrollWidth > bubbleClientWidth - horizontalPadding + 1
    || textScrollHeight > bubbleClientHeight - verticalPadding + 1;
}

export function calculateDraggedImagePosition({
  placement,
  naturalWidth,
  naturalHeight,
  frameWidth,
  frameHeight,
  deltaX,
  deltaY,
}: {
  placement: PanelPlacement;
  naturalWidth: number;
  naturalHeight: number;
  frameWidth: number;
  frameHeight: number;
  deltaX: number;
  deltaY: number;
}): Pick<PanelPlacement, "image_position_x" | "image_position_y"> {
  const scale = Math.max(frameWidth / naturalWidth, frameHeight / naturalHeight);
  const overflowX = naturalWidth * scale - frameWidth;
  const overflowY = naturalHeight * scale - frameHeight;
  const clamp = (value: number) => Math.max(0, Math.min(1, value));
  return {
    image_position_x: overflowX > 0
      ? clamp(placement.image_position_x - deltaX / overflowX)
      : placement.image_position_x,
    image_position_y: overflowY > 0
      ? clamp(placement.image_position_y - deltaY / overflowY)
      : placement.image_position_y,
  };
}

function BubbleLayer({ bubble, guide, selected, onSelect, onGesture }: {
  bubble: Bubble; guide: string; selected: boolean;
  onSelect?: PageLayerProps["onSelect"]; onGesture?: PageLayerProps["onGesture"];
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const element = textRef.current;
    const bubbleElement = element?.parentElement;
    if (!element || !bubbleElement) return;
    const measure = () => {
      const style = getComputedStyle(bubbleElement);
      setOverflow(textExceedsBubble({
        textScrollWidth: element.scrollWidth,
        textScrollHeight: element.scrollHeight,
        bubbleClientWidth: bubbleElement.clientWidth,
        bubbleClientHeight: bubbleElement.clientHeight,
        horizontalPadding: Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight),
        verticalPadding: Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom),
      }));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    observer.observe(bubbleElement);
    void document.fonts.ready.then(measure);
    measure();
    return () => observer.disconnect();
  }, [bubble]);
  const style = {
    left: `${bubble.x * 100}%`, top: `${bubble.y * 100}%`, width: `${bubble.width * 100}%`, height: `${bubble.height * 100}%`,
    zIndex: bubble.z_index, transform: `rotate(${bubble.rotation}deg)`, writingMode: bubble.writing_mode,
    fontFamily: bubble.style.font_family === "Noto Serif JP" ? "Comic Noto Serif" : "Comic Noto Sans",
    fontSize: bubble.style.font_size * (bubble.type === "small_note" ? 0.72 : 1), color: bubble.style.text_color,
    backgroundColor: bubble.style.background_color, borderColor: bubble.style.border_color, borderWidth: bubble.style.border_width,
    "--comic-bubble-background": bubble.style.background_color, "--comic-bubble-border": bubble.style.border_color,
    "--comic-bubble-border-width": `${bubble.style.border_width}px`,
  } as CSSProperties;
  return <button type="button" style={style} data-bubble-id={bubble.id}
    aria-label={bubble.text.trim() || `空の吹き出し ${bubble.reading_order}`}
    className={`comic-canvas-bubble comic-canvas-bubble--${bubble.type} comic-canvas-bubble--tail-${bubble.style.tail_direction}${selected ? " is-selected" : ""}${guide && overflow ? " comic-text-overflow" : ""}`}
    onClick={(event) => { event.stopPropagation(); onSelect?.({ kind: "bubble", id: bubble.id }); }}
    onPointerDown={(event) => onGesture?.(event, bubble, "drag")}>
    <span ref={textRef} className="comic-canvas-bubble__text">{bubble.text}</span>
    {guide && <span className="comic-reading-label">{guide}{overflow ? "・文字が枠外です" : ""}</span>}
    {onGesture && <span className="comic-canvas-bubble__resize" onPointerDown={(event) => onGesture(event, bubble, "resize")} />}
  </button>;
}

export function PageLayer({ state, panels, selection, showGuides, onSelect, onGesture, onPanelImageGesture }: PageLayerProps) {
  const sortedBubbles = [...state.bubbles].sort((left, right) => {
    const panelOrder = state.reading_order.indexOf(left.panel_id ?? "") - state.reading_order.indexOf(right.panel_id ?? "");
    return panelOrder || left.reading_order - right.reading_order;
  });
  return <div className="comic-page-render" style={{ width: state.page.width, height: state.page.height, padding:state.page.margin ?? 0, background: state.page.background }}
    onClick={() => onSelect?.({ kind: "page" })}>
    <style>{css}</style>
    <div className="comic-page-canvas__surface">
      {state.panel_placements.map((placement, placementIndex) => {
        const panel = panels.find((item) => item.id === placement.panel_id);
        const readingIndex = state.reading_order?.indexOf(placement.panel_id) ?? -1;
        return <button type="button" key={placement.panel_id} data-panel-id={placement.panel_id}
          aria-label={`コマ ${readingIndex >= 0 ? readingIndex + 1 : placementIndex + 1}`}
          data-image-fit={placement.image_fit}
          data-image-position-x={placement.image_position_x}
          data-image-position-y={placement.image_position_y}
          className={`comic-canvas-panel${placement.image_fit === "cover" && onPanelImageGesture ? " is-image-adjustable" : ""}${selection?.kind === "panel" && selection.id === placement.panel_id ? " is-selected" : ""}`}
          style={{ left: `${placement.x * 100}%`, top: `${placement.y * 100}%`, width: `${placement.width * 100}%`, height: `${placement.height * 100}%`, zIndex: placement.z_index }}
          onPointerDown={placement.image_fit === "cover" && onPanelImageGesture ? (event) => {
            onSelect?.({kind:"panel", id:placement.panel_id});
            onPanelImageGesture(event, placement);
          } : undefined}
          onClick={(event) => { event.stopPropagation(); onSelect?.({kind:"panel", id:placement.panel_id}); }}>
          {panel?.current_asset_url && <img src={panel.current_asset_url} alt="" draggable={false} style={{
            objectFit: placement.image_fit,
            objectPosition: `${placement.image_position_x * 100}% ${placement.image_position_y * 100}%`,
          }} />}
          {showGuides && <span className="comic-reading-label">コマ {state.reading_order.indexOf(placement.panel_id) + 1}</span>}
        </button>;
      })}
      {state.bubbles.map((bubble) => <BubbleLayer key={bubble.id} bubble={bubble}
        selected={selection?.kind === "bubble" && selection.id === bubble.id}
        guide={showGuides ? `${sortedBubbles.indexOf(bubble) + 1}${bubble.speaker_name ? ` ${bubble.speaker_name}` : ""}` : ""}
        onSelect={onSelect} onGesture={onGesture} />)}
    </div>
  </div>;
}

export function ResponsivePage(props: PageLayerProps) {
  const host = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(props.state.page.width);
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);
  return <div ref={host} style={{width:"100%", height:width * props.state.page.height / props.state.page.width, position:"relative"}}>
    <div style={{transform:`scale(${width / props.state.page.width})`, transformOrigin:"top left"}}><PageLayer {...props} /></div>
  </div>;
}
